import { useQuery } from '@tanstack/react-query';
import { makeBackendApi } from 'services/BackendApi';
import { TCurrencyCode } from 'interfaces';
import { ICurrencyExchangeResponse } from 'services/BackendApi/types/CurrencyExchange';

export const EXCHANGE_RATE_QUERY_KEY = 'exchangeRate';

/**
 * React Query hook for fetching exchange rate between two currencies.
 * Automatically returns 1 when currencies are the same (skips API call).
 *
 * @param fromCurrency - The source currency code
 * @param toCurrency - The target currency code
 * @param enabled - Whether the query should be enabled (default: true)
 * @returns Query object with exchange rate data
 */
export const useExchangeRateQuery = (
  fromCurrency: TCurrencyCode | null,
  toCurrency: TCurrencyCode | null,
  enabled: boolean = true
) => {
  const areSameCurrency = fromCurrency === toCurrency;
  const shouldFetch = enabled && !!fromCurrency && !!toCurrency && !areSameCurrency;

  return useQuery({
    queryKey: [EXCHANGE_RATE_QUERY_KEY, fromCurrency, toCurrency],
    queryFn: async (): Promise<number> => {
      if (!fromCurrency || !toCurrency) {
        throw new Error('Both currencies are required');
      }

      // If same currency, return 1 (no API call needed)
      if (areSameCurrency) {
        return 1;
      }

      const api = makeBackendApi();
      const response = await api.getExchangeRate({
        from: fromCurrency,
        to: toCurrency,
      });
      return (response.data as ICurrencyExchangeResponse).data.exchangeRate;
    },
    enabled: shouldFetch,
    staleTime: 60 * 1000, // Cache for 1 minute - exchange rates change frequently
  });
};
